import React, { forwardRef, useState } from "react";
import { Platform, Pressable, Text, TextInput, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { t } from "@/localization";
import HelpTip from "./HelpTip";
import useCopy from "./useCopy";
import { colors, control, radius, space, text } from "./theme";

// Form fields for settings screens. Every field has a visible label (not just a
// placeholder), an optional (?) help tip, a hint under the control, and an
// error that replaces the hint. Callers pass `error` only once the field was
// touched or a save was attempted: never while the user hasn't typed yet.

export const FieldLabel = ({
  label,
  help,
  optional,
  right,
  style,
  nativeID,
}) => (
  <View
    style={[tw`flex-row items-center`, { marginBottom: space.labelGap }, style]}
  >
    <View style={tw`flex-row items-center flex-1`}>
      <Text nativeID={nativeID} style={text.label}>
        {label}
        {optional && (
          <Text style={[text.hint, tw`font-normal`]}> {t("(optional)")}</Text>
        )}
      </Text>
      {!!help && <HelpTip topic={help} />}
    </View>
    {right}
  </View>
);

export const Hint = ({ children, style }) =>
  children ? (
    <Text style={[text.hint, { marginTop: space.labelGap }, style]}>
      {children}
    </Text>
  ) : null;

export const ErrorText = ({ children, style }) =>
  children ? (
    <View
      style={[tw`flex-row items-start`, { marginTop: space.labelGap }, style]}
      accessibilityLiveRegion="polite"
    >
      <Ionicons
        name="alert-circle"
        size={14}
        color={colors.danger}
        style={tw`mr-1 mt-[2px]`}
      />
      <Text style={[text.error, tw`flex-1`]}>{children}</Text>
    </View>
  ) : null;

// Label + any control + hint/error. `last` drops the bottom margin.
export const Field = ({
  label,
  help,
  optional,
  hint,
  error,
  right,
  children,
  style,
  last,
}) => (
  <View style={[!last && { marginBottom: space.field }, style]}>
    {!!label && (
      <FieldLabel label={label} help={help} optional={optional} right={right} />
    )}
    {children}
    {error ? <ErrorText>{error}</ErrorText> : <Hint>{hint}</Hint>}
  </View>
);

const inputBox = (focused, error, disabled) => [
  tw`flex-row items-center bg-white`,
  {
    minHeight: control.lg,
    borderRadius: radius.control,
    borderWidth: 1,
    borderColor: error ? colors.danger : focused ? colors.focus : colors.border,
  },
  focused &&
    !error &&
    Platform.OS === "web" && { boxShadow: "0 0 0 3px rgba(59,130,246,0.15)" },
  disabled && { backgroundColor: colors.surface },
];

// Text input in the settings style. `prefix` is fixed text before the value
// (e.g. "evt.live/"); `actions` are icon buttons inside the field's right edge:
// [{ icon, label, onPress }], or [{ icon: "copy-outline", label, copy: value,
// toast }] to copy with feedback (the icon turns into a check). `trailing` is
// a status shown at the right edge (e.g. a check when a link is available).
// `readOnly` shows the value as selectable text that wraps, and the box grows
// to fit it: a disabled one-line input on iOS let a long link run below the
// box.
export const TextInputBox = forwardRef(
  (
    {
      value,
      onChangeText,
      onBlur,
      onFocus,
      placeholder,
      multiline,
      rows = 4,
      prefix,
      actions,
      trailing,
      error,
      disabled,
      readOnly,
      inputStyle,
      style,
      onSubmitEditing,
      accessibilityLabel,
      ...rest
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const { copy, isCopied } = useCopy();
    return (
      <View
        style={[
          inputBox(focused, !!error, disabled || readOnly),
          multiline && tw`items-start`,
          style,
        ]}
      >
        {!!prefix && (
          <Text
            style={[
              tw`pl-3.5 text-[16px] font-semibold`,
              { color: colors.ink },
            ]}
            numberOfLines={1}
          >
            {prefix}
          </Text>
        )}
        {readOnly ? (
          <Text
            selectable
            accessibilityLabel={accessibilityLabel}
            style={[
              tw`flex-1 text-[16px] leading-[20px]`,
              {
                color: colors.muted,
                paddingHorizontal: prefix ? 0 : 14,
                // One line is as tall as the input: 48 minus the border.
                paddingVertical: 13,
              },
              prefix && tw`pr-3.5`,
              inputStyle,
            ]}
          >
            {value ?? ""}
          </Text>
        ) : (
          <TextInput
            ref={ref}
            value={value ?? ""}
            onChangeText={onChangeText}
            placeholder={placeholder}
            placeholderTextColor={colors.subtle}
            editable={!disabled}
            multiline={multiline}
            numberOfLines={multiline ? rows : 1}
            accessibilityLabel={accessibilityLabel || placeholder}
            onSubmitEditing={onSubmitEditing}
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
            style={[
              tw`flex-1 text-[16px] leading-[20px]`,
              {
                color: disabled ? colors.muted : colors.ink,
                paddingHorizontal: prefix ? 0 : 14,
              },
              prefix && tw`pr-3.5`,
              multiline
                ? {
                    minHeight: rows * 20 + 24,
                    paddingVertical: 12,
                    textAlignVertical: "top",
                  }
                : { height: control.lg - 2 },
              Platform.OS === "web" && { outlineStyle: "none" },
              inputStyle,
            ]}
            {...rest}
          />
        )}
        {!!trailing && <View style={tw`pr-3`}>{trailing}</View>}
        {!!actions?.length && (
          <View style={tw`flex-row items-center pr-1.5`}>
            {actions.map((action) => {
              const copied =
                action.copy !== undefined && isCopied(action.label);
              return (
                <Pressable
                  key={action.label}
                  onPress={
                    action.copy !== undefined
                      ? () =>
                          copy(action.copy, action.label, {
                            toast: action.toast || t("Copied"),
                            detail: action.toastDetail,
                          })
                      : action.onPress
                  }
                  accessibilityRole="button"
                  accessibilityLabel={copied ? t("Copied") : action.label}
                  hitSlop={6}
                  style={({ hovered, pressed }) => [
                    tw`p-2 rounded-md`,
                    (hovered || pressed) && {
                      backgroundColor: colors.surfaceStrong,
                    },
                  ]}
                >
                  <Ionicons
                    name={copied ? "checkmark" : action.icon}
                    size={20}
                    color={copied ? colors.success : colors.slate}
                  />
                </Pressable>
              );
            })}
          </View>
        )}
      </View>
    );
  }
);

// Field + TextInputBox.
export const TextField = forwardRef(
  (
    { label, help, optional, hint, error, right, last, fieldStyle, ...input },
    ref
  ) => (
    <Field
      label={label}
      help={help}
      optional={optional}
      hint={hint}
      error={error}
      right={right}
      last={last}
      style={fieldStyle}
    >
      <TextInputBox
        ref={ref}
        error={error}
        accessibilityLabel={label}
        {...input}
      />
    </Field>
  )
);

export default Field;
